import { ArrowsOutSimpleIcon, DotsThreeIcon, ShareNetworkIcon } from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { PreviewAvatar } from './PreviewAvatar';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  type PlatformPreviewBaseProps,
  resolveAspectRatio,
  resolveCarouselAspectRatio,
} from './types';

import { cn } from '@/lib/utils';

/**
 * Pinterest pin — tall image card with an overlaid red "Save" button on hover,
 * title underneath the image, board/board-owner row at the bottom. Replicates
 * a Pin in the home feed grid view.
 */
export const PinterestPin: React.FC<PlatformPreviewBaseProps & { boardName?: string }> = ({
  brandName,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  title,
  caption,
  imageUrl,
  imageAspectRatios,
  videoUrl,
  videoPoster,
  aspectRatio,
  focalX,
  focalY,
  zoom,
  isLoading = false,
  mediaOverlay,
  boardName,
  className,
}) => {
  // Single-image replica, so index 0 — but it must still read the per-child
  // array: the composer omits the scalar when the media's own ratio should
  // drive the frame, and reading only the scalar snapped every pin to 2:3.
  const resolvedAspect = resolveAspectRatio(
    resolveCarouselAspectRatio(aspectRatio, imageAspectRatios, 0),
    2 / 3,
  );

  return (
    <article className={cn('w-full group/pin', className)} data-platform="pinterest-pin">
      <div className="relative rounded-2xl overflow-hidden">
        <MediaSlot
          ratio={resolvedAspect}
          bg="muted"
          emptyHint="Your pin here"
          imageUrl={imageUrl}
          videoUrl={videoUrl}
          videoPoster={videoPoster}
          focalX={focalX}
          focalY={focalY}
          zoom={zoom}
          isLoading={isLoading}
          alt={title ?? `${brandName} pin`}
          overlay={mediaOverlay}
        />

        <div className="absolute top-2 left-2 right-2 flex items-center justify-between opacity-0 group-hover/pin:opacity-100 transition-opacity">
          <button
            type="button"
            className="rounded-full bg-background/80 backdrop-blur-sm size-8 flex items-center justify-center text-foreground hover:bg-background transition-colors"
            aria-label="Expand pin"
          >
            <ArrowsOutSimpleIcon className="size-4" weight="bold" />
          </button>
          {/* Platform-native CTA: Pinterest's own Save-button red, white label
              regardless of Mora's theme — this chrome isn't a Mora surface,
              so bg-background/text-foreground don't apply. Same treatment
              used throughout this directory for platform chrome (see
              FacebookPost.tsx's badge). Hover shade is Pinterest's own darker
              red, not a Mora surface either — new token, no existing token
              carries it. */}
          <button
            type="button"
            // eslint-disable-next-line no-restricted-syntax -- see comment above
            className="rounded-full bg-platform-pinterest text-white text-caption font-semibold px-4 h-8 hover:bg-platform-pinterest-hover transition-colors"
          >
            Save
          </button>
        </div>

        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-end gap-1 opacity-0 group-hover/pin:opacity-100 transition-opacity">
          <PinAction icon={<ShareNetworkIcon className="size-4" weight="bold" />} label="Share" />
          <PinAction icon={<DotsThreeIcon className="size-4" weight="bold" />} label="More" />
        </div>
      </div>

      {title || caption ? (
        <div className="mt-2 px-1 space-y-0.5">
          {title ? (
            <p className="text-sm font-semibold text-foreground line-clamp-2 leading-snug break-words">
              {title}
            </p>
          ) : null}
          {caption && !title ? (
            <p className="text-detail text-foreground/85 line-clamp-2 break-words">{caption}</p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-1.5 flex items-center gap-2 px-1">
        <PreviewAvatar
          brandName={brandName}
          brandColor={brandColor}
          avatarUrl={avatarUrl}
          className="size-6 rounded-full"
          textClassName="text-micro font-semibold"
        />
        <p className="text-detail text-foreground/85 truncate">
          <span className="font-medium">{brandName}</span>
          {boardName ? <span className="text-muted-foreground"> · {boardName}</span> : null}
        </p>
      </div>
    </article>
  );
};

const PinAction: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <button
    type="button"
    aria-label={label}
    // eslint-disable-next-line no-restricted-syntax -- pre-existing, out of scope for this pass (not a hardcoded brand hex): Pinterest's own white pin-action chrome, not a Mora surface.
    className="rounded-full bg-white/95 backdrop-blur-sm size-8 flex items-center justify-center text-foreground hover:bg-white transition-colors"
  >
    {icon}
  </button>
);
